import { Component, Story, Knobs, Icons, Accents } from './DocTypes';

const PlaygroundKnobs: Knobs = {
  icon: {
    default: 'Folder',
    editor: 'dropdown',
    editorOptions: Icons,
  },
  text: {
    default: 'File',
    editor: 'textbox',
  },
  menuArrowAccent: {
    default: 'title',
    editor: 'dropdown',
    editorOptions: Accents,
  },
};

const PlaygroundKnobsV3: Knobs = {
  ...PlaygroundKnobs,
  tippyProps: {
    editor: 'textbox',
    default: '',
  },
  suppressMenuArrow: {
    editor: 'checkbox',
    default: false,
  },
  suppressHoverHighlight: {
    editor: 'checkbox',
    default: false,
  },
  colourScheme: {
    editor: 'dropdown',
    editorOptions: ['light', 'dark', 'default', ''],
    default: '',
  },
  device: {
    editor: 'dropdown',
    editorOptions: ['mobile', 'desktop', ''],
    default: '',
  },
};

const PlaygroundKnobsV3_React: Knobs = {
  ...PlaygroundKnobsV3,
  tippyProps: {
    ...PlaygroundKnobsV3.tippyProps,
    qualifierStart: '{',
    qualifierEnd: '}',
  },
};

const PlaygroundStory: Story = {
  title: 'Playground',
  description: 'A tag-menu where you can change all the attributes',
  coreComponent: {
    html: `<tag-menu{{...knobs}}>
  <tag-menu-item name='open' text='Open'></tag-menu-item>
  <tag-menu-item name='exit' text='Exit' description='Only use when you want to exit the whole app'></tag-menu-item>
</tag-menu>`,
    knobs: PlaygroundKnobs,
  },
  coreComponent_v3: {
    html: `
    <tag3-menu {{...knobs}}>
      <tag3-menu-item name="open" {{colour-scheme}}>
        <div slot="menu-item-header">Open</div>
      </tag3-menu-item>
      <tag3-menu-item name="exit" description="Only use when you want to exit the whole app" {{colour-scheme}}>
        <div slot="menu-item-header">Exit</div>
      </tag3-menu-item>
    </tag3-menu>`,
    knobs: PlaygroundKnobsV3,
  },
  reactWrapper: {
    jsx: `<TagMenu{{...knobs}}
  items={[
    { name: 'open', text: 'Open' },
    { name: 'exit', text: 'Exit', description: 'Only use when you want to exit the whole app' },
  ]}
  />`,
    knobs: PlaygroundKnobs,
  },
  reactWrapper_v3: {
    jsx: `
    <Tag3Menu {{...knobs}}>
      <Tag3MenuItem name="open">
        <div slot="menu-item-header">Open</div>
      </Tag3MenuItem>
      <Tag3MenuItem name="exit" description="Only use when you want to exit the whole app">
        <div slot="menu-item-header">Exit</div>
      </Tag3MenuItem>
    </Tag3Menu>`,
    knobs: PlaygroundKnobsV3_React,
  },
};

const WithIconsStory: Story = {
  title: 'Menu with icons',
  description: 'A tag-menu with icons',
  coreComponent: {
    html: `<tag-menu text="Menu with icons">
  <tag-menu-item name='Option1' text='Option 1' icon='Bug'></tag-menu-item>
  <tag-menu-item name='Option2' text='Option 2' icon='AddressBook'></tag-menu-item>
</tag-menu>`,
  },
  coreComponent_v3: {
    html: `
    <tag3-menu>
      <div slot="menu-header">
        Menu with icons
      </div>
      <tag3-menu-item name="Option1">
        <div slot="menu-item-header" style="display: flex;align-items: center;">
          <tag3-icon-svg icon="Bug" style="padding-right: 5px"></tag3-icon-svg>
          Option 1
        </div>
      </tag3-menu-item>
      <tag3-menu-item name="Option2">
        <div slot="menu-item-header" style="display: flex;align-items: center;">
          <tag3-icon-svg icon="AddressBook" style="padding-right: 5px"></tag3-icon-svg>
          Option 2
        </div>
      </tag3-menu-item>
    </tag3-menu>`,
  },
  reactWrapper: {
    jsx: `<TagMenu
  text="Menu with icons"
  items={[
    { name: 'Option1', text: 'Option 1', icon: 'Bug' },
    { name: 'Option2', text: 'Option 2', icon: 'AddressBook' },
  ]}
/>`,
  },
  reactWrapper_v3: {
    jsx: `
    <Tag3Menu>
      <div slot="menu-header">Menu with icons</div>
      <Tag3MenuItem name="Option1">
        <div slot="menu-item-header" style={{display: 'flex', alignItems: 'center'}}>
          <Tag3IconSvg icon="Bug" style={{paddingRight: '5px'}} />
          Option 1
        </div>
      </Tag3MenuItem>
      <Tag3MenuItem name="Option2">
        <div slot="menu-item-header" style={{display: 'flex', alignItems: 'center'}}>
          <Tag3IconSvg icon="AddressBook" style={{paddingRight: '5px'}} />
          Option 2
        </div>
      </Tag3MenuItem>
    </Tag3Menu>`,
  },
};

const SubmenuStory: Story = {
  title: 'Submenu',
  description: 'A tag-menu with a submenu',
  coreComponent: {
    html: `<tag-menu icon='Settings' text='Settings'>
  <tag-menu-item name='preferences' text='Preferences' is-submenu submenu-position='right-start'>
    <tag-menu-item name='color' text='Color' is-submenu submenu-position='right-start'>
      <tag-menu-item name='red' text='Red' icon='Art' icon-accent='access'></tag-menu-item>
      <tag-menu-item name='green' text='Green' icon='Art' icon-accent='keppel'></tag-menu-item>
      <tag-menu-item name='blue' text='Blue' icon='Art' icon-accent='atoll'></tag-menu-item>
    </tag-menu-item>
    <tag-menu-item name='align' text='Align' is-submenu submenu-position='right-start'>
      <tag-menu-item name='alignLeft' text='Align Left' icon='AlignL'></tag-menu-item>
      <tag-menu-item name='alignCenter' text='Align Center' icon='AlignC'></tag-menu-item>
      <tag-menu-item name='alignRight' text='Align Right' icon='AlignR'></tag-menu-item>
    </tag-menu-item>
  </tag-menu-item>
  <tag-menu-item name='about' text='About'></tag-menu-item>
  <tag-menu-item name='exit' text='Exit'></tag-menu-item>
</tag-menu>`,
  },
  coreComponent_v3: {
    html: `
    <tag3-menu>
      <div slot="menu-header" style="display: flex; align-items:center; padding: 3px">
        <tag3-icon-svg icon="Settings"></tag3-icon-svg>
        Settings
      </div>
      <tag3-menu-item is-submenu name="preferences">
        <div slot="menu-item-header">Prefereces</div>
        <tag3-menu-item is-submenu name="color">
          <div slot="menu-item-header">Color</div>
          <tag3-menu-item name="red">
            <div slot="menu-item-header" style="display: flex;align-items: center;">
              <tag3-icon-svg icon="Art" accent="access" style="padding-right: 5px"></tag3-icon-svg>
              Red
            </div>
          </tag3-menu-item>
          <tag3-menu-item name="green">
            <div slot="menu-item-header" style="display: flex;align-items: center;">
              <tag3-icon-svg icon="Art" accent="keppel" style="padding-right: 5px"></tag3-icon-svg>
              Green
            </div>
          </tag3-menu-item>
          <tag3-menu-item name="blue">
            <div slot="menu-item-header" style="display: flex;align-items: center;">
              <tag3-icon-svg icon="Art" accent="atoll" style="padding-right: 5px"></tag3-icon-svg>
              Blue
            </div>
          </tag3-menu-item>
        </tag3-menu-item>
        <tag3-menu-item is-submenu name="align">
          <div slot="menu-item-header">Align</div>
          <tag3-menu-item name="alignLeft">
            <div slot="menu-item-header" style="display: flex;align-items: center;">
              <tag3-icon-svg icon="AlignL" style="padding-right: 5px"></tag3-icon-svg>
              Align Left
            </div>
          </tag3-menu-item>
          <tag3-menu-item name="alignCenter">
            <div slot="menu-item-header" style="display: flex;align-items: center;">
              <tag3-icon-svg icon="AlignC" style="padding-right: 5px"></tag3-icon-svg>
              Align Center
            </div>
          </tag3-menu-item>
          <tag3-menu-item name="alignRight">
            <div slot="menu-item-header" style="display: flex;align-items: center;">
              <tag3-icon-svg icon="AlignR" style="padding-right: 5px"></tag3-icon-svg>
              Align Right
            </div>
          </tag3-menu-item>
        </tag3-menu-item>
      </tag3-menu-item>
      <tag3-menu-item name="about">
        <div slot="menu-item-header">About</div>
      </tag3-menu-item>
      <tag3-menu-item name="exit">
        <div slot="menu-item-header">Exit</div>
      </tag3-menu-item>
    </tag3-menu>`,
  },
  reactWrapper: {
    jsx: `<TagMenu
  icon='Settings'
  text='Settings'
  items={[
    {
      isSubmenu: true,
      name: 'preferences',
      nestedItems: [
        {
          isSubmenu: true,
          name: 'color',
          nestedItems: [
            { name: 'red', text: 'Red', icon:'Art', iconAccent:'access'},
            { name: 'green', text: 'Green', icon:'Art', iconAccent:'keppel'},
            { name: 'blue', text: 'Blue', icon:'Art', iconAccent:'atoll'},
          ],
          submenuPosition:'right-start',
          text: 'Color',
        },
        {
          isSubmenu: true,
          name: 'align',
          nestedItems: [
            { name: 'alignLeft', text: 'Align Left', icon:'AlignL'},
            { name: 'alignCenter', text: 'Align Center', icon:'AlignC'},
            { name: 'alignRight', text: 'Align Right', icon:'AlignR'},
          ],
          submenuPosition:'right-start',
          text: 'Align',
        },
      ],
      submenuPosition:'right-start',
      text: 'Preferences',
    },
    { name: 'about', text: 'About', },
    { name: 'exit', text: 'Exit', },
  ]}

/>`,
  },
  reactWrapper_v3: {
    jsx: `
    <Tag3Menu>
      <div slot="menu-header" style={{display: 'flex', alignItems: 'center', padding: '3px'}}>
        <Tag3IconSvg icon="Settings" />
        Settings
      </div>
      <Tag3MenuItem is-submenu name="preferences">
        <div slot="menu-item-header">Prefereces</div>
        <Tag3MenuItem is-submenu name="color">
          <div slot="menu-item-header">Color</div>
          <Tag3MenuItem name="red">
            <div slot="menu-item-header" style={{display: 'flex', alignItems: 'center'}}>
              <Tag3IconSvg icon="Art" accent="access" style={{paddingRight: '5px'}} />
              Red
            </div>
          </Tag3MenuItem>
          <Tag3MenuItem name="green">
            <div slot="menu-item-header" style={{display: 'flex', alignItems: 'center'}}>
              <Tag3IconSvg icon="Art" accent="keppel" style={{paddingRight: '5px'}} />
              Green
            </div>
          </Tag3MenuItem>
          <Tag3MenuItem name="blue">
            <div slot="menu-item-header" style={{display: 'flex', alignItems: 'center'}}>
              <Tag3IconSvg icon="Art" accent="atoll" style={{paddingRight: '5px'}} />
              Blue
            </div>
          </Tag3MenuItem>
        </Tag3MenuItem>
        <Tag3MenuItem is-submenu name="align">
          <div slot="menu-item-header">Align</div>
          <Tag3MenuItem name="alignLeft">
            <div slot="menu-item-header" style={{display: 'flex', alignItems: 'center'}}>
              <Tag3IconSvg icon="AlignL" style={{paddingRight: '5px'}} />
              Align Left
            </div>
          </Tag3MenuItem>
          <Tag3MenuItem name="alignCenter">
            <div slot="menu-item-header" style={{display: 'flex', alignItems: 'center'}}>
              <Tag3IconSvg icon="AlignC" style={{paddingRight: '5px'}} />
              Align Center
            </div>
          </Tag3MenuItem>
          <Tag3MenuItem name="alignRight">
            <div slot="menu-item-header" style={{display: 'flex', alignItems: 'center'}}>
              <Tag3IconSvg icon="AlignR" style={{paddingRight: '5px'}} />
              Align Right
            </div>
          </Tag3MenuItem>
        </Tag3MenuItem>
      </Tag3MenuItem>
      <Tag3MenuItem name="about">
        <div slot="menu-item-header">About</div>
      </Tag3MenuItem>
      <Tag3MenuItem name="exit">
        <div slot="menu-item-header">Exit</div>
      </Tag3MenuItem>
    </Tag3Menu>`,
  },
};

const DynamicSubMenusStory: Story = {
  title: 'Dynamic Submenu',
  description: 'A tag-menu with a dynamically loaded submenu',
  coreComponent: {
    html: `
<tag-menu text="Settings" icon="Settings">
  <tag-menu-item text="Preferences" is-submenu>
    <tag-menu-item text="Color" is-subMenu id="colorMenu">
    </tag-menu-item>
    <tag-menu-item text="Align" is-subMenu id="alignMenu">
    </tag-menu-item>
  </tag-menu-item>
  <tag-menu-item text="About"></tag-menu-item>
  <tag-menu-item text="Exit"></tag-menu-item>
</tag-menu>
<p id="loaded"></p>
    `,
    js: `
var colorData = [
  {text: 'Red', icon: 'Art', iconAccent: 'access'},
  {text: 'Green', icon: 'Art', iconAccent: 'keppel'},
  {text: 'Blue', icon: 'Art', iconAccent: 'atoll'}
];

var alignData = [
  {text: 'Align Left', icon: 'AlignL'},
  {text: 'Align Center', icon: 'AlignC'},
  {text: 'Align Right', icon: 'AlignR'}
]

var colorMenu = document.querySelector('tag-menu-item[id="colorMenu"]');
var alignMenu = document.querySelector('tag-menu-item[id="alignMenu"]');

if (timeout) {
  clearTimeout(timeout)
}

var timeout = setTimeout(function() {
  for (var i = 0; i < colorData.length; i++) {
      var item = colorData[i];
      var element = document.createElement("tag-menu-item");
      element.text = item.text;
      element.icon = item.icon;
      element.iconAccent= item.iconAccent;
      colorMenu.appendChild(element);
    }

    for (var i = 0; i < alignData.length; i++) {
      var item = alignData[i];
      var element = document.createElement("tag-menu-item");
      element.text = item.text;
      element.icon = item.icon;
      alignMenu.appendChild(element);
    }

  document.getElementById('loaded').innerHTML = 'Loaded';
}, 2000)

`,
  },
  coreComponent_v3: {
    html: `
<tag3-menu text="Settings" icon="Settings">
  <tag3-menu-item text="Preferences" is-submenu>
    <tag3-menu-item text="Color" is-subMenu id="colorMenu">
    </tag3-menu-item>
    <tag3-menu-item text="Align" is-subMenu id="alignMenu">
    </tag3-menu-item>
  </tag3-menu-item>
  <tag3-menu-item text="About"></tag3-menu-item>
  <tag3-menu-item text="Exit"></tag3-menu-item>
</tag3-menu>

<p id="loaded"></p>
    `,
    js: `
var colorData = [
  {text: 'Red', icon: 'Art', iconAccent: 'access'},
  {text: 'Green', icon: 'Art', iconAccent: 'keppel'},
  {text: 'Blue', icon: 'Art', iconAccent: 'atoll'}
];

var alignData = [
  {text: 'Align Left', icon: 'AlignL'},
  {text: 'Align Center', icon: 'AlignC'},
  {text: 'Align Right', icon: 'AlignR'}
]

var colorMenu = document.querySelector('tag3-menu-item[id="colorMenu"]');
var alignMenu = document.querySelector('tag3-menu-item[id="alignMenu"]');


if (timeout) {
  clearTimeout(timeout)
}

var timeout = setTimeout(function() {
  for (var i = 0; i < colorData.length; i++) {
    var item = colorData[i];
    var element = document.createElement("tag3-menu-item");
    element.text = item.text;
    element.icon = item.icon;
    element.iconAccent= item.iconAccent;
    colorMenu.appendChild(element);
  }

  for (var i = 0; i < alignData.length; i++) {
    var item = alignData[i];
    var element = document.createElement("tag3-menu-item");
    element.text = item.text;
    element.icon = item.icon;
    alignMenu.appendChild(element);
  }

  document.getElementById('loaded').innerHTML = 'Loaded';
}, 2000)

`,
  },
  reactWrapper: {
    jsx: `<Example/>`,
    parentJSX: `Example = () => {
      const [menuItems, setMenuItems] = React.useState();

      React.useEffect(() => {
        setTimeout(() => {
          setMenuItems([
            {text: 'Preference',
            isSubmenu: true,
            nestedItems: [
              {
                text: 'color',
                isSubmenu: true,
                nestedItems: [
                  { name: 'red', text: 'Red', icon:'Art', iconAccent:'access'},
                  { name: 'green', text: 'Green', icon:'Art', iconAccent:'keppel'},
                  { name: 'blue', text: 'Blue', icon:'Art', iconAccent:'atoll'},
                ]
              },
              {
                text: "Align",
                isSubmenu: true,
                nestedItems: [
                  {text: 'Align Left', icon: 'AlignL'},
                  {text: 'Align Center', icon: 'AlignC'},
                  {text: 'Align Right', icon: 'AlignR'}
                ]
              }
            ]
          },
          {
            text: 'About',
          },
          {
            text: 'Exit',

          }
        ])
        },2000)
      },[])
      return (
        <div>
          <TagMenu text="Settings" icon="Settings" items={menuItems} />
          {menuItems && <p>Data loaded</p>}
        </div>
      )
    }`,
  },
  reactWrapper_v3: {
    jsx: `<Example/>`,
    parentJSX: `Example = () => {
      const [menuItems, setMenuItems] = React.useState();

      React.useEffect(() => {
        setTimeout(() => {
          setMenuItems([
            {
              text: 'Color',
              isSubmenu: true,
              name: 'Color',
              nestedItems: [
                { name: 'red', text: 'Red', icon:'Art', iconAccent:'access'},
                { name: 'green', text: 'Green', icon:'Art', iconAccent:'keppel'},
                { name: 'blue', text: 'Blue', icon:'Art', iconAccent:'atoll'},
              ]
            },
            {
              text: "Align",
              isSubmenu: true,
              name: 'Align',
              nestedItems: [
                { name: 'alignLeft', text: 'Align Left', icon:'AlignL'},
                { name: 'alignCenter', text: 'Align Center', icon:'AlignC'},
                { name: 'alignRight', text: 'Align Right', icon:'AlignR'},
              ],
            }
          ])
        },2000)
      },[])
      return (
        <div>
          <Tag3Menu text="Settings" icon="Settings">
            <Tag3MenuItem text="Preferences" isSubmenu={true} nestedItems={menuItems} />
            <Tag3MenuItem text="About" />
            <Tag3MenuItem text="Exit" />
          </Tag3Menu>
          {menuItems && <p>Data loaded</p>}
        </div>
      )
    }`,
  },
};

const DynamicSubMenuChildrenStory: Story = {
  title: 'Dynamic React Children menu',
  description: "A tag-menu with a dynamically loaded submenu' using react children",
  reactWrapper_v3: {
    jsx: `<Example/>`,
    parentJSX: `Example = () => {
      const [menuItems, setMenuItems] = React.useState();

      React.useEffect(() => {
        setTimeout(() => {
          setMenuItems([
            {
              text: 'color',
              isSubmenu: true,
              nestedItems: [
                { name: 'red', text: 'Red', icon:'Art', iconAccent:'access'},
                { name: 'green', text: 'Green', icon:'Art', iconAccent:'keppel'},
                { name: 'blue', text: 'Blue', icon:'Art', iconAccent:'atoll'},
              ]
            },
            {
              text: "Align",
              isSubmenu: true,
              nestedItems: [
                { name: 'alignLeft', text: 'Align Left', icon:'AlignL'},
                { name: 'alignCenter', text: 'Align Center', icon:'AlignC'},
                { name: 'alignRight', text: 'Align Right', icon:'AlignR'},
              ],
            }
          ])
        },2000)
      },[])
      return (
        <div>
          <Tag3Menu text="Settings" icon="Settings">
            <Tag3MenuItem text="Preferences" isSubmenu={true}>
              {menuItems && menuItems.map(item =>
                <Tag3MenuItem text={item.text} icon={item.icon} isSubmenu={item.nestedItems ? true :  undefined}>
                  {item.nestedItems && item.nestedItems.map(nested => <Tag3MenuItem text={nested.text} icon={nested.icon} iconAccent={nested.iconAccent}></Tag3MenuItem>)}
                </Tag3MenuItem>)}
            </Tag3MenuItem>
            <Tag3MenuItem text="About" />
            <Tag3MenuItem text="Exit" />
          </Tag3Menu>
          {menuItems && <p>Data loaded</p>}
        </div>
      )
    }`,
  },
};

export const TagMenuDocs: Component = {
  type: 'component',
  name: 'menu',
  description: 'Renders a menu',
  mobileFriendly: false,
  popular: false,
  preRelease: 'beta',
  props: [
    {
      name: 'icon',
      description: 'An icon to display to the left of the menu',
      type: 'string',
    },
    {
      name: 'text',
      description: 'The menu text',
      type: 'string',
    },
    {
      name: 'menuHeaderStyle',
      description: 'JSX style object in JSON to apply to the menu header',
      type: 'string',
    },
    {
      name: 'menuItemContainerStyle',
      description: 'JSX style object in JSON to apply to the menu item container',
      type: 'string',
    },
    {
      name: 'menuArrowAccent',
      description: 'The accent of the menu dropdown arrow',
      type: 'Accent',
    },
    {
      name: 'menuItemStyle',
      description: 'JSX style object in JSON to apply to the menu item',
      type: 'string',
    },
    {
      name: 'tippyProps',
      default: '',
      description: 'Only V3: Defines the tippy props.',
      type: 'string',
    },
    {
      name: 'suppressMenuArrow',
      description: 'V3 Only: Control to hide the menu arrow icon.',
      type: 'boolean',
    },
    {
      name: 'suppressHoverHighlight',
      description: 'V3 Only: Whether the hover highlight style is used or not.',
      type: 'boolean',
    },
    {
      name: 'colour-scheme',
      description:
        'V3 Only: Dark mode (theme) control. Default for normal browser driven control. Dark | Light to force respective styling regardless of browser setting.',
      type: 'dark | light | default',
      default: 'light',
    },
    {
      name: 'device',
      description: 'V3 Only: Set preferred styling theme for device type.',
      type: 'desktop | mobile',
      default: 'desktop',
    },
  ],
  reactPropOverrides: [],
  events: [
    {
      name: 'menuItemClick',
      description: 'Triggers when a menu item clicked',
      parameters: [
        {
          name: 'name',
          description: 'The name attribute of the menu item',
          type: 'string',
        },
        {
          name: 'text',
          description: 'The text attribute of the menu item',
          type: 'string',
        },
      ],
    },
  ],
  methods: [],
  stories: [
    PlaygroundStory,
    WithIconsStory,
    SubmenuStory,
    DynamicSubMenusStory,
    DynamicSubMenuChildrenStory,
  ],
};
